import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useSuperAdmin } from '../../context/SuperAdminContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Alert, AlertDescription } from '../../components/ui/alert';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Shield, Smartphone, Mail, Key, Loader2, CheckCircle, AlertTriangle,
  Copy, Download, QrCode
} from 'lucide-react';

const TwoFactorSetup = () => {
  const { isDark = true } = useOutletContext() || {};
  const { api, admin } = useSuperAdmin();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [setupMethod, setSetupMethod] = useState(null); // 'totp' or 'email'
  const [totpData, setTotpData] = useState(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [backupCodes, setBackupCodes] = useState(null);
  const [showDisableDialog, setShowDisableDialog] = useState(false);
  const [disableCode, setDisableCode] = useState('');

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      const res = await api.get('/api/super-admin/auth/2fa/status');
      setStatus(res.data);
    } catch (err) {
      console.error('Failed to fetch 2FA status:', err);
    } finally {
      setLoading(false);
    }
  };

  const startTotpSetup = async () => {
    setSubmitting(true);
    setError('');
    try {
      const res = await api.post('/api/super-admin/auth/2fa/setup-totp');
      setTotpData(res.data);
      setSetupMethod('totp');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to start setup');
    } finally {
      setSubmitting(false);
    }
  };

  const startEmailSetup = async () => {
    setSubmitting(true);
    setError('');
    try {
      await api.post('/api/super-admin/auth/2fa/enable-email');
      setSetupMethod('email');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send code');
    } finally {
      setSubmitting(false);
    }
  };

  const verifyAndEnable = async () => {
    if (!verifyCode || verifyCode.length !== 6) {
      setError('Please enter a 6-digit code');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const endpoint = setupMethod === 'totp' 
        ? '/api/super-admin/auth/2fa/enable-totp'
        : '/api/super-admin/auth/2fa/confirm-email';
      const res = await api.post(endpoint, { code: verifyCode });
      setBackupCodes(res.data.backup_codes);
      fetchStatus();
    } catch (err) {
      setError(err.response?.data?.detail || 'Verification failed');
    } finally {
      setSubmitting(false);
    }
  };

  const disable2FA = async () => {
    if (!disableCode || disableCode.length !== 6) {
      setError('Please enter a 6-digit code');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await api.post('/api/super-admin/auth/2fa/disable', { code: disableCode });
      setShowDisableDialog(false);
      setDisableCode('');
      setSetupMethod(null);
      setTotpData(null);
      setBackupCodes(null);
      fetchStatus();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to disable 2FA');
    } finally {
      setSubmitting(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const downloadBackupCodes = () => {
    if (!backupCodes) return;
    const content = `EQUIPR CORE Backup Codes\n${'='.repeat(30)}\n\n${backupCodes.join('\n')}\n\nKeep these codes safe. Each code can only be used once.`;
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'equipr-backup-codes.txt';
    a.click();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#ffd108]"></div>
      </div>
    );
  }

  // Show backup codes after setup
  if (backupCodes) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-green-500/20 rounded-full">
            <CheckCircle className="h-8 w-8 text-green-500" />
          </div>
          <div>
            <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>2FA Enabled Successfully!</h1>
            <p className={isDark ? 'text-slate-400' : 'text-gray-500'}>Save your backup codes before continuing</p>
          </div>
        </div>

        <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200 shadow-sm'}>
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Key className="h-5 w-5 text-[#ffd108]" />
              Backup Codes
            </CardTitle>
            <CardDescription className={isDark ? 'text-slate-400' : 'text-gray-500'}>
              Save these codes in a safe place. Each code can only be used once to access your account if you lose your authenticator.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-2 mb-4 p-4 bg-slate-900 rounded-lg font-mono">
              {backupCodes.map((code, i) => (
                <div key={i} className="text-white text-center py-1">{code}</div>
              ))}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => copyToClipboard(backupCodes.join('\n'))}>
                <Copy className="mr-2 h-4 w-4" /> Copy All
              </Button>
              <Button variant="outline" onClick={downloadBackupCodes}>
                <Download className="mr-2 h-4 w-4" /> Download
              </Button>
            </div>
          </CardContent>
        </Card>

        <Button onClick={() => setBackupCodes(null)} className="bg-[#ffd108] text-black hover:bg-[#e5bc07]">
          I've Saved My Codes
        </Button>
      </div>
    );
  }

  // Setup flow
  if (setupMethod) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Set Up Two-Factor Authentication</h1>
          <p className={isDark ? 'text-slate-400' : 'text-gray-500'}>
            {setupMethod === 'totp' ? 'Scan the QR code with your authenticator app' : 'Enter the code sent to your email'}
          </p>
        </div>

        <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200 shadow-sm'}>
          <CardContent className="pt-6">
            {error && (
              <Alert variant="destructive" className="mb-4">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {setupMethod === 'totp' && totpData && (
              <div className="space-y-4">
                <div className="flex justify-center">
                  <div className="bg-white p-4 rounded-lg">
                    <img src={totpData.qr_code} alt="QR Code" className="w-48 h-48" />
                  </div>
                </div>
                <div className="text-center">
                  <p className="text-sm text-slate-400 mb-2">Or enter this key manually:</p>
                  <div className="flex items-center justify-center gap-2">
                    <code className="bg-slate-900 px-3 py-1 rounded text-[#ffd108] font-mono text-sm">
                      {totpData.manual_entry_key}
                    </code>
                    <Button size="sm" variant="ghost" onClick={() => copyToClipboard(totpData.manual_entry_key)}>
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {setupMethod === 'email' && (
              <div className="text-center mb-4">
                <Mail className="h-12 w-12 mx-auto mb-2 text-[#ffd108]" />
                <p className={isDark ? 'text-slate-300' : 'text-gray-700'}>Check your email for the verification code</p>
                <p className="text-sm text-slate-500">(Check console/logs for mock email)</p>
              </div>
            )}

            <div className="space-y-4 mt-6">
              <div className="space-y-2">
                <Label className={isDark ? 'text-slate-300' : 'text-gray-700'}>Enter 6-digit code</Label>
                <Input
                  value={verifyCode}
                  onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  className="bg-slate-700 border-slate-600 text-white text-center text-2xl tracking-widest font-mono"
                  maxLength={6}
                />
              </div>
              <div className="flex gap-2">
                <Button variant="ghost" onClick={() => { setSetupMethod(null); setTotpData(null); setVerifyCode(''); setError(''); }}>
                  Cancel
                </Button>
                <Button
                  onClick={verifyAndEnable}
                  disabled={submitting || verifyCode.length !== 6}
                  className="flex-1 bg-[#ffd108] text-black hover:bg-[#e5bc07]"
                >
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Verify & Enable'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Main status view
  return (
    <div className="space-y-6">
      <div>
        <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Two-Factor Authentication</h1>
        <p className={isDark ? 'text-slate-400' : 'text-gray-500'}>Add an extra layer of security to your account</p>
      </div>

      {/* Current Status */}
      <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200 shadow-sm'}>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={`p-3 rounded-full ${status?.two_fa_enabled ? 'bg-green-500/20' : 'bg-slate-700'}`}>
                <Shield className={`h-6 w-6 ${status?.two_fa_enabled ? 'text-green-500' : 'text-slate-400'}`} />
              </div>
              <div>
                <h3 className="font-medium text-white">Two-Factor Authentication</h3>
                <p className="text-sm text-slate-400">
                  {status?.two_fa_enabled 
                    ? `Enabled via ${status.two_fa_method === 'totp' ? 'Authenticator App' : 'Email'}`
                    : 'Not enabled'}
                </p>
              </div>
            </div>
            {status?.two_fa_enabled ? (
              <Badge className="bg-green-500/20 text-green-400">Enabled</Badge>
            ) : (
              <Badge className="bg-orange-500/20 text-orange-400">Disabled</Badge>
            )}
          </div>
        </CardContent>
      </Card>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {!status?.two_fa_enabled ? (
        /* Setup Options */
        <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200 shadow-sm'}>
          <CardHeader>
            <CardTitle className={isDark ? 'text-white' : 'text-gray-900'}>Choose Your 2FA Method</CardTitle>
            <CardDescription className={isDark ? 'text-slate-400' : 'text-gray-500'}>
              Select how you want to receive your verification codes
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Authenticator App */}
              <div
                onClick={startTotpSetup}
                className="p-4 border border-slate-600 rounded-lg cursor-pointer hover:border-[#ffd108] hover:bg-slate-700/50 transition-all"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-[#ffd108]/20 rounded-lg">
                    <Smartphone className="h-5 w-5 text-[#ffd108]" />
                  </div>
                  <div>
                    <h4 className="font-medium text-white">Authenticator App</h4>
                    <Badge variant="outline" className="text-xs">Recommended</Badge>
                  </div>
                </div>
                <p className="text-sm text-slate-400">
                  Use Google Authenticator, Authy, or similar apps to generate codes
                </p>
              </div>

              {/* Email */}
              <div
                onClick={startEmailSetup}
                className="p-4 border border-slate-600 rounded-lg cursor-pointer hover:border-[#ffd108] hover:bg-slate-700/50 transition-all"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-blue-500/20 rounded-lg">
                    <Mail className="h-5 w-5 text-blue-400" />
                  </div>
                  <h4 className="font-medium text-white">Email Code</h4>
                </div>
                <p className="text-sm text-slate-400">
                  Receive a verification code via email each time you sign in
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : (
        /* Already enabled - show disable option */
        <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200 shadow-sm'}>
          <CardHeader>
            <CardTitle className={isDark ? 'text-white' : 'text-gray-900'}>Manage 2FA</CardTitle>
          </CardHeader>
          <CardContent>
            <Button variant="destructive" onClick={() => setShowDisableDialog(true)}>
              Disable Two-Factor Authentication
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Disable Dialog */}
      <Dialog open={showDisableDialog} onOpenChange={setShowDisableDialog}>
        <DialogContent className="bg-slate-800 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-orange-400" />
              Disable Two-Factor Authentication
            </DialogTitle>
            <DialogDescription className={isDark ? 'text-slate-400' : 'text-gray-500'}>
              This will make your account less secure. Enter your current 2FA code to confirm.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            <div className="space-y-2">
              <Label className={isDark ? 'text-slate-300' : 'text-gray-700'}>
                {status?.two_fa_method === 'totp' ? 'Authenticator Code' : 'Email Code'}
              </Label>
              <Input
                value={disableCode}
                onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                className="bg-slate-700 border-slate-600 text-white text-center text-xl tracking-widest font-mono"
                maxLength={6}
              />
              {status?.two_fa_method === 'email' && (
                <Button variant="link" size="sm" onClick={startEmailSetup} className="text-[#ffd108]">
                  Send new code
                </Button>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setShowDisableDialog(false); setDisableCode(''); setError(''); }}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={disable2FA} disabled={submitting || disableCode.length !== 6}>
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Disable 2FA'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TwoFactorSetup;
